import React, { useEffect, useState, useCallback } from "react";
import { CONSTANTS as C } from "config";
import BrandingPreviewMenuWidget from "./Widgets";
import BrandingPlatformCustomizeAndWidget from "./CustomiseBranding";
import { useHistory } from "react-router-dom";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { UTILS } from "utils";
import { useTranslation } from "react-i18next";
import {
  useGetSettingsMutation,
  useUpdateBrandSettingsMutation,
  useUpdateDomainNameMutation,
  useUpdateBrandNameMutation,

} from "redux/settings/settingsSlice";
import { useDispatch, useSelector } from "react-redux";
import { LoaderAction } from "redux/actions";
import { UPDATE_BRAND_SETTINGS } from "api/api";
import { errorHandler } from "api/api/errorHandler";
import AppContainer from "components/ui/AppContainer";
import AppButton from "components/ui/AppButton";

const stripHex = (c) =>
  c && typeof c === "string" ? c.replace(/^#/, "") : c;

const uploadResponseUrl = (file) => {
  const r = file?.response;
  if (!r) return null;
  if (typeof r === "string") return r;
  if (typeof r.data === "string") return r.data;
  return r.data?.url ?? r.url ?? null;
};

const Branding = ({ onSaveAndContinue }) => {
  const router = useHistory();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const { t } = useTranslation();
  const [updateBrandSettings] = useUpdateBrandSettingsMutation();
  const [updateDomainName] = useUpdateDomainNameMutation();
  const [getSettings] = useGetSettingsMutation();
  const [updateBrandName] = useUpdateBrandNameMutation();
  const [imgLoading, setImgLoading] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [settings, setSettings] = useState({
    brandImageHomeBG: "",
    brandVideoHomeBG: "",
    brandLogoFirst: "",
    taglineEN: "",
    titleEN: "",
    sloganEN: "",
    color: "",
    textColor: "",
  });
  const [orderNowPopupConfig, setOrderNowPopupConfig] = useState({
    iconUrl: "",
    message: "",
    buttonText: "",
    title: "",
  });

  const fetchSettings = useCallback(async () => {
    const userDetail = UTILS.getCurrentUser();
    const obj = {
      brandId: userDetail.brandId,
      language: "EN",
    };
    const data = await getSettings(obj).unwrap();
    if (data?.success === 1) {
      if (data?.data?.setting?.orderNowPopupConfig) {
        setOrderNowPopupConfig(data.data.setting.orderNowPopupConfig);
      }
      let next = { ...data.data.setting };
      next.showBannerImage = next?.showBannerImage?.toString();
      next.showBannerVideo = next?.showBannerVideo?.toString();
      next.enableStoreListingView = next?.enableStoreListingView?.toString();
      if (data?.data?.setting?.color) {
        next.color = "#" + data.data.setting.color;
        if (data?.data?.setting?.textColor) {
          next.textColor = "#" + data.data.setting.textColor;
        }
      } else {
        next.color = C.SETTINGS_DEFAULT_COLOUR;
        next.textColor = C.DEFAULT_TEXT_COLOUR;
      }
      setSettings(next);
    }
  }, [getSettings]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  function beforeUpload(file) {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      UTILS.errorNotification(t("You can only upload JPG/PNG file"));
    }
    const F_Size = file.size / 1024;
    const isLt1M = F_Size <= C.MAX_IMAGE_SIZE;
    if (!isLt1M) {
      UTILS.errorNotification(t("Image must be smaller than 2MB"));
    }
    return isJpgOrPng && isLt1M;
  }

  const uploadButton = (
    <div>
      {imgLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{t("Upload")}</div>
    </div>
  );

  const onPreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow.document.write(image.outerHTML);
  };

  const handleChange = async (info, type) => {
    // if (info.file.status === "uploading") {
    //   setImgLoading(true);
    //   return;
    // }
    // if (info.file.status === "error") {
    //   setImgLoading(false);
    //   UTILS.errorNotification(t("Upload failed"));
    //   return;
    // }
    // if (info.file.status === "removed") {
    //   setImgLoading(false);
    //   if (type === "iconUrl") {
    //     setOrderNowPopupConfig((prev) => ({ ...prev, iconUrl: "" }));
    //   } else {
    //     setSettings((prev) => ({ ...prev, [type]: "" }));
    //   }
    //   return;
    // }

    if (info.actionType === "added") {
      setImgLoading(false);
      const uploadedUrl = info.file.url;
      // if (!uploadedUrl) {
      //   UTILS.errorNotification(t("Upload failed"));
      //   return;
      // }

      const userDetail = UTILS.getCurrentUser();
      try {
        if (type === "iconUrl") {
          const nextPopup = { ...orderNowPopupConfig, iconUrl: uploadedUrl };
          setOrderNowPopupConfig(nextPopup);
          await updateBrandSettings({
            ...settings,
            color: stripHex(settings.color),
            textColor: stripHex(settings.textColor),
            brandId: userDetail.brandId,
            orderNowPopupConfig: nextPopup,
          }).unwrap();
        } else {
          const nextSettings = { ...settings, [type]: uploadedUrl };
          setSettings(nextSettings);
          await updateBrandSettings({
            ...nextSettings,
            color: stripHex(nextSettings.color),
            textColor: stripHex(nextSettings.textColor),
            brandId: userDetail.brandId,
            orderNowPopupConfig,
          }).unwrap();
        }
        await fetchSettings();
      } catch (e) {
        UTILS.errorNotification(errorHandler(e));
      }
    }
  };

  const updateMyBrand = async () => {
    const userDetail = UTILS.getCurrentUser();
    const brandName = settings?.browserTabTitle?.trim();
    if (!brandName || userDetail?.brandName === brandName) {
      return true;
    }
    const obj = {
      brandId: userDetail.brandId,
      brandName,
      suppressSuccessNotification: true,
    };
    const response = await updateBrandName(obj);
    const data = response?.data;

    if (data?.success === 1) {
      UTILS.saveCurrentUser({
        ...userDetail,
        brandName,
      });
      return true;
    }
    UTILS.errorNotification(errorHandler(response?.error || data?.message));
    return false;
  };

  const saveSettings = async () => {
    if (!UTILS.validateAdminSetting(orderNowPopupConfig)) {
      return;
    }
    if (
      settings?.temporaryDomainName &&
      !UTILS.isDomainNameValid(settings.temporaryDomainName)
    ) {
      UTILS.errorNotification(t("Please enter a valid domain name"));
      return;
    }
    const userDetail = UTILS.getCurrentUser();
    let obj = {
      ...settings,
      color: stripHex(settings.color),
      textColor: stripHex(settings.textColor),
      orderNowPopupConfig,
      brandId: userDetail.brandId,
    };
    try {
      dispatch(LoaderAction.setLoading(true));
      const isBrandUpdated = await updateMyBrand();
      if (!isBrandUpdated) {
        return;
      }
      if (settings?.temporaryDomainName) {
        const domainResponse = await updateDomainName({
          brandId: userDetail.brandId,
          temporaryDomainName: settings.temporaryDomainName,
        });
        if (domainResponse?.data?.success === 1) {
          UTILS.saveCurrentUser({
            ...userDetail,
            temporaryDomainName: settings.temporaryDomainName,
          });
        } else {
          UTILS.errorNotification(errorHandler(domainResponse?.error || domainResponse?.data?.message));
          return;
        }
      }
      if (Object.prototype.hasOwnProperty.call(obj, "merchantSavings")) {
        delete obj.merchantSavings;
      }
      if (obj.video && obj.video instanceof File) {
        delete obj.brandVideoHomeBG;
      }
      const result = await UPDATE_BRAND_SETTINGS(obj, ["video", "mediaToDelete"]);

      if (result?.success === 1) {
        UTILS.successNotification(t("Settings Updated"));
        if (typeof onSaveAndContinue === "function") {
          onSaveAndContinue();
        }
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
      await fetchSettings();
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
       <AppButton variant="outline" onClick={() => router.goBack()}>
          {t("Cancel")}
        </AppButton>
      <AppButton variant="primary" onClick={() => saveSettings()}>
        {t("Save & Continue")}
      </AppButton>
     
    </div>
  );

  return (
    <AppContainer
      title={t("Platform customization")}
      subTitle={t(
        "Change the appearance of your ordering platform and make it look like your business."
      )}
      loading={isLoading || imgLoading}
      footer={footer}
    >
      <div className="brand-identity-container flex flex-col gap-6 lg:flex-row lg:gap-8">
        <div className="brand-identity-container-c1 w-full min-w-0 lg:w-2/3">
          <BrandingPlatformCustomizeAndWidget
            setSettings={setSettings}
            settings={settings}
            orderNowPopupConfig={orderNowPopupConfig}
            setOrderNowPopupConfig={setOrderNowPopupConfig}
            handleChange={handleChange}
            onPreview={onPreview}
            uploadButton={uploadButton}
            beforeUpload={beforeUpload}
          />
        </div>
        <div className="brand-identity-container-c1 w-full min-w-0 lg:w-1/3">
          <BrandingPreviewMenuWidget
            key={previewNonce}
            settings={settings}
            orderNowPopupConfig={orderNowPopupConfig}
            onPreviewRefresh={() => setPreviewNonce((n) => n + 1)}
          />
        </div>
      </div>
    </AppContainer>
  );
};

export default Branding;
